import { useState, useEffect, Suspense, lazy } from 'react';
import { SBIPublicHeader } from './components/SBIPublicHeader';
import { Footer } from './components/Footer';
import { MAIN_SITE_URL } from './lib/constants';

/**
 * portfolio-sbi App
 *
 * Full UX replacement for the deprecated legacy portfolio worker (retired #1392).
 * Uses FloatingHeader from @smallbiz/brand-kit/components for visual parity
 * with www-sbi, admin-sbi, and all other SBI services.
 *
 * Routing is handled via browser History API (no React Router dependency)
 * — same lightweight pattern used in admin-sbi.
 */

const HomePage = lazy(() => import('./pages/HomePage'));
const DetailPage = lazy(() => import('./pages/DetailPage'));

const normalizePath = (p: string) => p.replace(/\/+$/, '') || '/';

type ViewState = { mode: 'list' } | { mode: 'detail'; slug: string };

function getViewFromPath(pathname: string): ViewState {
  const path = normalizePath(pathname);
  if (path.startsWith('/portfolio/')) {
    return { mode: 'detail', slug: path.replace('/portfolio/', '') };
  }
  // Support /:slug as a legacy direct path too
  if (path !== '/' && !path.startsWith('/portfolio')) {
    const slug = path.replace('/', '');
    if (slug && !slug.includes('/')) {
      return { mode: 'detail', slug };
    }
  }
  return { mode: 'list' };
}

export default function App() {
  const [currentPath, setCurrentPath] = useState(() =>
    typeof window !== 'undefined' ? normalizePath(window.location.pathname) : '/'
  );

  useEffect(() => {
    const handler = () => setCurrentPath(normalizePath(window.location.pathname));
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!document.documentElement.getAttribute('data-theme')) {
      document.documentElement.setAttribute('data-theme', 'storm');
    }
  }, []);

  function navigate(href: string | null) {
    // null means "go back to the list"
    const target = href ?? '/';
    if (target.startsWith('http')) {
      window.open(target, '_blank', 'noopener,noreferrer');
      return;
    }
    // Only portfolio-owned paths stay within portfolio-sbi; everything else belongs to the main site
    if (target === '/' || target === '/portfolio' || target.startsWith('/portfolio/')) {
      window.history.pushState({}, '', target);
      setCurrentPath(normalizePath(target));
      // Scroll to top on navigation
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    // Redirect all other paths to the main SmallBiz Innovation site
    window.location.href = `${MAIN_SITE_URL}${target}`;
  }

  const navItems: Array<{ name: string; href: string; isActive: boolean }> = [];

  const view = getViewFromPath(currentPath);

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors">
      <SBIPublicHeader navItems={navItems} onNavigate={navigate} />

      {/* Main content with top padding to prevent header overlap */}
      <main className="pt-20 sm:pt-24">
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-muted-foreground">Loading…</p>
              </div>
            </div>
          }
        >
          {view.mode === 'detail' ? (
            <DetailPage slug={view.slug} onNavigate={navigate} />
          ) : (
            <HomePage onNavigate={navigate} />
          )}
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}
